import AddRoundedIcon from "@mui/icons-material/AddRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import {
  Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent,
  DialogTitle, FormControlLabel, IconButton, InputAdornment, Menu,
  MenuItem, Paper, Stack, Switch, TextField, Typography,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { SoftDataGrid } from "../../../components/data/SoftDataGrid";
import { PermissionGate } from "../../../components/auth/PermissionGate";
import {
  createCurrency, deleteCurrency, getCurrencies, updateCurrency,
} from "../../../services/currenciesService";
import { getApiErrorMessage } from "../../../utils/apiData";

const fieldSx = {
  "& .MuiInputBase-root": { minHeight: 38, borderRadius: 0, fontSize: "0.72rem" },
  "& .MuiInputLabel-root": { fontSize: "0.72rem" },
  "& .MuiFormHelperText-root": { mx: 0, fontSize: "0.63rem" },
};

function extractPaginator(payload) {
  return payload?.currencies ?? payload?.data?.currencies ?? payload?.data ?? payload ?? {};
}

function extractRows(payload) {
  const paginator = extractPaginator(payload);
  if (Array.isArray(paginator?.data)) return paginator.data;
  if (Array.isArray(paginator)) return paginator;
  return [];
}

function initials(code) {
  return String(code || "CU").slice(0, 2).toUpperCase();
}

function CurrencyForm({ currency, busy, onCancel, onSubmit }) {
  const { control, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      code: currency?.code || "",
      name: currency?.name || "",
      symbol: currency?.symbol || "",
      decimal_places: currency?.decimal_places ?? 2,
      is_active: currency?.is_active ?? true,
    },
  });

  return (
    <Box component="form" onSubmit={handleSubmit((values) => onSubmit({
      code: values.code.trim().toUpperCase(),
      name: values.name.trim(),
      symbol: values.symbol.trim() || null,
      decimal_places: Number(values.decimal_places),
      is_active: Boolean(values.is_active),
    }))}>
      <Stack spacing={1.2}>
        <Controller name="code" control={control} rules={{ required: "Currency code is required." }} render={({ field }) => (
          <TextField {...field} label="Currency code" placeholder="USD" inputProps={{ maxLength: 10 }}
            onChange={(event) => field.onChange(event.target.value.toUpperCase())}
            error={Boolean(errors.code)} helperText={errors.code?.message || "The code must be unique."} sx={fieldSx} />
        )} />
        <Controller name="name" control={control} rules={{ required: "Currency name is required." }} render={({ field }) => (
          <TextField {...field} label="Currency name" placeholder="United States Dollar" error={Boolean(errors.name)} helperText={errors.name?.message} sx={fieldSx} />
        )} />
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, gap: 1.2 }}>
          <Controller name="symbol" control={control} render={({ field }) => (
            <TextField {...field} label="Symbol" placeholder="$" sx={fieldSx} />
          )} />
          <Controller name="decimal_places" control={control} rules={{ min: 0, max: 6 }} render={({ field }) => (
            <TextField {...field} type="number" label="Decimal places" inputProps={{ min: 0, max: 6 }} sx={fieldSx} />
          )} />
        </Box>
        <Controller name="is_active" control={control} render={({ field }) => (
          <FormControlLabel control={<Switch size="small" checked={Boolean(field.value)} onChange={(_, checked) => field.onChange(checked)} />}
            label={<Typography sx={{ fontSize: "0.7rem", fontWeight: 700 }}>Active currency</Typography>} />
        )} />
      </Stack>
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
        <Button color="inherit" variant="outlined" onClick={onCancel} disabled={busy} sx={{ borderRadius: 0 }}>Cancel</Button>
        <Button type="submit" variant="contained" disabled={busy} sx={{ borderRadius: 0 }}>
          {busy ? "Saving..." : currency ? "Save changes" : "Create currency"}
        </Button>
      </Stack>
    </Box>
  );
}

function RowMenu({ row, onView, onEdit, onDelete }) {
  const [anchor, setAnchor] = useState(null);
  const choose = (handler) => { setAnchor(null); handler(row); };
  return <>
    <IconButton size="small" onClick={(event) => setAnchor(event.currentTarget)}><MoreVertRoundedIcon sx={{ fontSize: 17 }} /></IconButton>
    <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} PaperProps={{ sx: { borderRadius: 0, minWidth: 180 } }}>
      <MenuItem onClick={() => choose(onView)} sx={{ gap: 1, fontSize: "0.7rem" }}><VisibilityOutlinedIcon sx={{ fontSize: 16 }} />View details</MenuItem>
      <PermissionGate permission="update_currencies"><MenuItem onClick={() => choose(onEdit)} sx={{ gap: 1, fontSize: "0.7rem" }}><EditOutlinedIcon sx={{ fontSize: 16 }} />Edit currency</MenuItem></PermissionGate>
      <PermissionGate permission="delete_currencies"><MenuItem onClick={() => choose(onDelete)} sx={{ gap: 1, color: "error.main", fontSize: "0.7rem" }}><DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />Delete currency</MenuItem></PermissionGate>
    </Menu>
  </>;
}

export function CurrenciesPage() {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 20 });
  const [dialog, setDialog] = useState({ type: null, currency: null });
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearch(searchInput.trim());
      setPaginationModel((current) => ({ ...current, page: 0 }));
    }, 450);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  const query = useQuery({
    queryKey: ["currencies", search, paginationModel.page, paginationModel.pageSize],
    queryFn: () => getCurrencies({ search: search || undefined, page: paginationModel.page + 1, per_page: paginationModel.pageSize }),
    placeholderData: (previous) => previous,
  });

  const paginator = extractPaginator(query.data);
  const rows = extractRows(query.data);

  const saveMutation = useMutation({
    mutationFn: ({ currency, values }) => currency ? updateCurrency(currency.id, values) : createCurrency(values),
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries({ queryKey: ["currencies"] });
      queryClient.invalidateQueries({ queryKey: ["currency-options"] });
      setDialog({ type: null, currency: null });
      setMessage({ title: variables.currency ? "Currency updated" : "Currency created", text: response?.message || "Currency saved successfully." });
    },
    onError: (error) => setMessage({ title: "Unable to save currency", text: getApiErrorMessage(error) }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteCurrency,
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ["currencies"] });
      queryClient.invalidateQueries({ queryKey: ["currency-options"] });
      setDialog({ type: null, currency: null });
      setMessage({ title: "Currency deleted", text: response?.message || "Currency deleted successfully." });
    },
    onError: (error) => setMessage({ title: "Unable to delete currency", text: getApiErrorMessage(error) }),
  });

  const columns = useMemo(() => [
    { field: "avatar", headerName: "", width: 54, sortable: false, renderCell: (params) => <Avatar sx={{ width: 28, height: 28, bgcolor: "#EEF3FF", color: "#2F5BEA", fontSize: "0.6rem", fontWeight: 850 }}>{initials(params.row.code)}</Avatar> },
    { field: "code", headerName: "Code", minWidth: 110, flex: 0.45, renderCell: (params) => <Typography sx={{ fontSize: "0.72rem", fontWeight: 850 }}>{params.row.code}</Typography> },
    { field: "name", headerName: "Currency name", minWidth: 220, flex: 1 },
    { field: "symbol", headerName: "Symbol", minWidth: 90, valueGetter: (_, row) => row.symbol || "—" },
    { field: "decimal_places", headerName: "Decimals", width: 90 },
    { field: "status", headerName: "Status", width: 90, renderCell: (params) => <Chip label={params.row.is_active ? "Active" : "Inactive"} size="small" sx={{ height: 22, borderRadius: 0, bgcolor: params.row.is_active ? "#E7F6EE" : "#F1F3F5", color: params.row.is_active ? "#18794E" : "#667085", fontSize: "0.65rem", fontWeight: 750 }} /> },
    { field: "created_at", headerName: "Created", minWidth: 130, valueFormatter: (value) => value ? new Date(value).toLocaleDateString("en-ZA") : "—" },
    { field: "actions", headerName: "", width: 54, sortable: false, renderCell: (params) => <RowMenu row={params.row} onView={(currency) => setDialog({ type: "view", currency })} onEdit={(currency) => setDialog({ type: "edit", currency })} onDelete={(currency) => setDialog({ type: "delete", currency })} /> },
  ], []);

  return <Box sx={{ p: { xs: 1.2, md: 2 } }}>
    <Paper variant="outlined" sx={{ borderRadius: 0 }}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ p: 1.2, borderBottom: "1px solid #E3E7EC" }}>
        <Box sx={{ flex: 1 }}><Typography sx={{ fontSize: "0.82rem", fontWeight: 850 }}>Currencies</Typography><Typography sx={{ mt: 0.25, color: "#667085", fontSize: "0.66rem" }}>Manage currency codes used by Shop Wi-Fi rules.</Typography></Box>
        <TextField size="small" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search code, name or symbol" sx={{ width: { xs: "100%", md: 300 }, "& .MuiInputBase-root": { height: 36, borderRadius: 0, fontSize: "0.7rem" } }} InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 16 }} /></InputAdornment> }} />
        <PermissionGate permission="create_currencies"><Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setDialog({ type: "create", currency: null })} sx={{ borderRadius: 0 }}>New currency</Button></PermissionGate>
      </Stack>
      <SoftDataGrid rows={rows} columns={columns} loading={query.isLoading} rowCount={paginator?.total ?? rows.length} paginationMode="server" paginationModel={paginationModel} onPaginationModelChange={setPaginationModel} pageSizeOptions={[10, 20, 50, 100]} getRowId={(row) => row.id} />
    </Paper>

    <Dialog open={dialog.type === "create" || dialog.type === "edit"} onClose={() => setDialog({ type: null, currency: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
      <DialogTitle sx={{ borderBottom: "1px solid #E3E7EC", fontSize: "0.9rem", fontWeight: 850 }}>{dialog.type === "edit" ? "Edit currency" : "Create currency"}</DialogTitle>
      <DialogContent sx={{ p: 2.2, bgcolor: "#FBFCFD" }}><CurrencyForm key={`${dialog.type}-${dialog.currency?.id || "new"}`} currency={dialog.type === "edit" ? dialog.currency : null} busy={saveMutation.isPending} onCancel={() => setDialog({ type: null, currency: null })} onSubmit={(values) => saveMutation.mutate({ currency: dialog.type === "edit" ? dialog.currency : null, values })} /></DialogContent>
    </Dialog>

    <Dialog open={dialog.type === "view"} onClose={() => setDialog({ type: null, currency: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
      <DialogTitle sx={{ fontSize: "0.9rem", fontWeight: 850 }}>Currency details</DialogTitle>
      <DialogContent dividers><Stack spacing={0.8}><Typography sx={{ fontWeight: 850 }}>{dialog.currency?.code} — {dialog.currency?.name}</Typography><Typography sx={{ fontSize: "0.7rem" }}>Symbol: {dialog.currency?.symbol || "—"}</Typography><Typography sx={{ fontSize: "0.7rem" }}>Decimal places: {dialog.currency?.decimal_places ?? 2}</Typography><Typography sx={{ fontSize: "0.7rem" }}>Status: {dialog.currency?.is_active ? "Active" : "Inactive"}</Typography></Stack></DialogContent>
      <DialogActions><Button onClick={() => setDialog({ type: null, currency: null })} variant="contained" sx={{ borderRadius: 0 }}>Close</Button></DialogActions>
    </Dialog>

    <Dialog open={dialog.type === "delete"} onClose={() => setDialog({ type: null, currency: null })} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
      <DialogTitle sx={{ fontSize: "0.9rem", fontWeight: 850 }}>Delete currency</DialogTitle>
      <DialogContent dividers><Typography sx={{ fontSize: "0.72rem" }}>Delete <strong>{dialog.currency?.code}</strong>? A currency used by a Wi-Fi rule cannot be deleted.</Typography></DialogContent>
      <DialogActions><Button color="inherit" onClick={() => setDialog({ type: null, currency: null })} sx={{ borderRadius: 0 }}>Cancel</Button><Button color="error" variant="contained" disabled={deleteMutation.isPending} onClick={() => deleteMutation.mutate(dialog.currency.id)} sx={{ borderRadius: 0 }}>{deleteMutation.isPending ? "Deleting..." : "Delete currency"}</Button></DialogActions>
    </Dialog>

    <Dialog open={Boolean(message)} onClose={() => setMessage(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 0 } }}>
      <DialogTitle sx={{ fontSize: "0.82rem", fontWeight: 850 }}>{message?.title}</DialogTitle>
      <DialogContent dividers><Typography sx={{ fontSize: "0.7rem" }}>{message?.text}</Typography></DialogContent>
      <DialogActions><Button variant="contained" onClick={() => setMessage(null)} sx={{ borderRadius: 0 }}>OK</Button></DialogActions>
    </Dialog>
  </Box>;
}
